import axios from '../helpers/axios';
import { logError } from '../services';
import {
  IResponse,
  IDropdownResponse,
  IBuildCommunication,
  IBuildCommunicationQueryParams,
  IBuildPhoto,
  IBuildCommunicationInfo,
  IBuildMessage,
  IBuildMessageQueryParams,
  MessageReadStatus
} from '../models';

const baseUrl = '/api/production/build-communications';

export const getBuildCommunications = async (
  filters?: IBuildCommunicationQueryParams
): Promise<IResponse<IBuildCommunication[]>> => {
  try {
    const { data } = await axios.get(baseUrl, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunications failed');
    return Promise.reject(error);
  }
};

export const getBuildCommunicationLots = async (
  neighborhoodId: number | string,
  filters: {
    restrictToOpenBuilds: boolean;
    restrictToBuildsWithUnread?: boolean | null;
  }
): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/neighborhoods/${neighborhoodId}/lots`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunicationLots failed');
    return Promise.reject(error);
  }
};

export const getBuildCommunicationNeighborhoods = async (filters: {
  restrictToOpenBuilds: boolean;
  restrictToBuildsWithUnread?: boolean | null;
}): Promise<IDropdownResponse[]> => {
  try {
    const { data } = await axios.get(`${baseUrl}/neighborhoods`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunicationNeighborhoods failed');
    return Promise.reject(error);
  }
};

export const getBuildCommunicationPhotos = async (
  buildId: number,
  filters?: {
    sortBy?: string;
    sortDirection?: string;
    page?: number;
    perPage?: number;
  }
): Promise<IResponse<IBuildPhoto[]>> => {
  try {
    const { data } = await axios.get(`${baseUrl}/build/${buildId}/photos`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunicationPhotos failed');
    return Promise.reject(error);
  }
};

export const uploadBuildCommunicationPhoto = async (
  buildId: number,
  formData: FormData
): Promise<void> => {
  try {
    await axios.post(`${baseUrl}/build/${buildId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  } catch (error) {
    logError(error, 'uploadBuildCommunicationPhoto failed');
    return Promise.reject(error);
  }
};

export const getBuildCommunicationInfo = async (
  buildId: number
): Promise<IBuildCommunicationInfo> => {
  try {
    const { data } = await axios.get(`${baseUrl}/build/${buildId}/info`);
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunicationInfo failed');
    return Promise.reject(error);
  }
};

export const getBuildCommunicationMessages = async (
  buildId: number,
  filters?: IBuildMessageQueryParams
): Promise<IResponse<IBuildMessage[]>> => {
  try {
    const { data } = await axios.get(`${baseUrl}/build/${buildId}/messages`, {
      params: filters
    });
    return data;
  } catch (error) {
    logError(error, 'getBuildCommunicationMessages failed');
    return Promise.reject(error);
  }
};

export const postBuildCommunicationMessage = async (
  buildId: number,
  formData: FormData
): Promise<void> => {
  try {
    await axios.post(`${baseUrl}/build/${buildId}/messages`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  } catch (error) {
    logError(error, 'postBuildCommunicationMessage failed');
    return Promise.reject(error);
  }
};

export const markBuildCommunicationMessageAsRead = async (
  buildId: number,
  messageId: number,
  messageStatus?: MessageReadStatus
): Promise<void> => {
  try {
    const params = messageStatus ? { messageStatus } : {};
    await axios.put(`${baseUrl}/build/${buildId}/messages/${messageId}/read`, null, {
      params
    });
  } catch (error) {
    logError(error, 'markBuildCommunicationMessageAsRead failed');
    return Promise.reject(error);
  }
};
